import { ChangeDetectionStrategy, Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { fadeInOutAnimation } from '@newroom-connect/library/animations';
import { Level } from '@newroom-connect/library/enums';
import { IFileHydrated } from '@newroom-connect/library/interfaces';
import { FileHelper, ImageHelper } from '@newroom-connect/library/helpers';
import { TranslatePipe, StartsWithPipe } from '@newroom-connect/library/pipes';
import { LoggingService, ToastService, TranslationService } from '@newroom-connect/library/services';

import { VideoComponent } from '../../../video/video.component';
import { IconComponent } from '../../../icon/icon.component';
import { TooltipComponent } from '../../../tooltip/tooltip.component';
import { ImageComponent } from '../../../image/image.component';

@Component({
  selector: 'nrc-area-background-2d',
  standalone: true,
  imports: [
    CommonModule,
    VideoComponent,
    ImageComponent,
    IconComponent,
    TooltipComponent,
    TranslatePipe,
    StartsWithPipe
  ],
  animations: [fadeInOutAnimation],
  templateUrl: './area-background-2d.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AreaBackground2DComponent implements OnChanges, OnInit {
  @Input({ required: true }) public apiBaseUrl = '';
  @Input() public backgroundFile?: IFileHydrated;
  @Input() public isEditMode = false;

  @Output() public mediaLoaded = new EventEmitter<void>();

  public source = signal<string>('');
  public thumbnailSource = signal<string>('');
  public alt = signal<string>('');
  public mimetype = signal<string>('');
  public backgroundMeetsRequirements = signal<boolean>(true);

  /**
   * @constructor
   *
   * @param loggingService
   * @param toastService
   * @param translationService
   */
  constructor(
    private readonly loggingService: LoggingService,
    private readonly toastService: ToastService,
    private readonly translationService: TranslationService
  ) { }

  /**
   * Initialize the background state and handle 16:9 aspect ratio alert.
   */
  public ngOnInit(): void {
    if (!this.backgroundFile) {
      return;
    }

    this.handleBackgroundFile(this.backgroundFile);
  }

  /**
   *
   * @param changes
   */
  public ngOnChanges(changes: SimpleChanges): void {
    // Process background file changes
    if (changes['backgroundFile'] && this.backgroundFile && !changes['backgroundFile'].firstChange) {
      this.handleBackgroundFile(this.backgroundFile);
    }
  }

  /**
   *
   */
  public onMediaLoadComplete(): void {
    this.validateImageDimensions();
    this.mediaLoaded.emit();
  }

  /**
   * Open a toast message warning about image aspect ratio.
   */
  public openAspectRatioWarningMessage(): void {
    if (!this.isEditMode) {
      return;
    }

    this.toastService.setMessage({
      message: this.translationService.translate('COMPONENTS.AREAS.AREA_BACKGROUND.AREA_BACKGROUND_2D.TOAST.ASPECT_RATIO_WARNING'),
      level: Level.WARN
    });
  }

  /**
   * Validate image dimensions using existing file metadata.
   */
  private validateImageDimensions(): void {
    if (!this.backgroundFile || !this.backgroundFile.width || !this.backgroundFile.height) {
      return;
    }

    const meetsRequirements = ImageHelper.doesImageMeetDimensions(this.backgroundFile);

    this.backgroundMeetsRequirements.set(meetsRequirements);

    if (!meetsRequirements) {
      this.openAspectRatioWarningMessage();
    }
  }

  /**
   * Handle a background file where the image properties are being set.
   * Also, check if the given background image meets the specified requirements and set the appropriate signal.
   *
   * @param backgroundFile The background file object to handle.
   */
  private handleBackgroundFile(backgroundFile: IFileHydrated): void {
    try {
      this.source.set(FileHelper.getFileSourceURI(this.apiBaseUrl, backgroundFile));
      this.alt.set(backgroundFile.filenameDisk);
      this.mimetype.set(backgroundFile.mimetype);
      this.thumbnailSource.set(backgroundFile.thumbnail ?? '');
    } catch (error) {
      this.loggingService.error('Unable to handle background file in 2D context: ', error);
    }
  }
}
